import React, { useState, useEffect, useCallback, useRef } from 'react';
import LoanSelectorDialog from './LoanSelectorDialog';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    Grid,
    Autocomplete,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Tooltip from '@mui/material/Tooltip';
import SchoolIcon from '@mui/icons-material/School';
import PageLayout from './PageLayout';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import FactCheckIcon from '@mui/icons-material/FactCheck';


// Configure PDF.js worker.
pdfjs.GlobalWorkerOptions.workerSrc = process.env.PUBLIC_URL + '/pdf.worker.min.mjs';

// SimplePdfViewer – renders each page with its page number above an HR.
function SimplePdfViewer({ downloadUrl, startPage = 1, endPage, onLoadSuccess, onPageChange, zoomLevel = 1.0, rotateLevel = 0 }) {

    const [numPages, setNumPages] = useState(null);
    const containerRef = useRef(null);
    const pageRefs = useRef({});   

    const handleDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        if (onLoadSuccess) onLoadSuccess(numPages);
    };

    let pagesToRender = [];
    if (numPages) {
        const from = startPage;
        const to = endPage && endPage <= numPages ? endPage : numPages;
        for (let i = from; i <= to; i++) {
            pagesToRender.push(i);
        }
    }

    return (
        <Box ref={containerRef} sx={{ overflowY: 'auto', position: 'relative' }}>
            <Document file={downloadUrl} onLoadSuccess={handleDocumentLoadSuccess}>
                {pagesToRender.map((pageNum) => (
                    <Box
                        key={`page_${pageNum}`}
                        data-page={pageNum}
                        ref={(el) => (pageRefs.current[pageNum] = el)}
                        sx={{ mb: 2, cursor: 'pointer' }}
                        onClick={() => {
                            if (onPageChange) onPageChange(pageNum);
                        }}
                    >
                        <Page
                            pageNumber={pageNum}
                            width={600}                // fixed base width
                            scale={zoomLevel}          // let PDF.js scale for us
                            rotate={rotateLevel}
                            renderTextLayer
                            renderAnnotationLayer
                        />
                        <Typography
                            sx={{
                                fontSize: '0.8rem',
                                backgroundColor: '#ccc',
                                display: 'inline-block',
                                padding: '2px 4px',
                                mt: 1,
                                mb: 0.5,
                            }}
                        >
                            Page {pageNum}
                        </Typography>
                        <hr style={{ border: 0, borderTop: '1px solid #ccc' }} />
                    </Box>
                ))}
            </Document>
        </Box>
    );
}

// EditClassificationDialog – uses Autocomplete for searchable classification selection.
function EditClassificationDialog({ open, onClose, onConfirm, initialValue, classificationOptions }) {
    const [selectedOption, setSelectedOption] = useState(initialValue);

    useEffect(() => {
        setSelectedOption(initialValue);
    }, [initialValue]);

    // Format the option label using the "label" and "alias" properties.
    const formatOptionLabel = (option) => {
        return `${option.label} (${option.alias || ''})`;
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Edit Classification</DialogTitle>
            <DialogContent>
                <Autocomplete
                    options={classificationOptions}
                    getOptionLabel={(option) => formatOptionLabel(option)}
                    value={selectedOption}
                    onChange={(event, newValue) => setSelectedOption(newValue)}
                    renderInput={(params) => (
                        <TextField {...params} label="Classification" variant="outlined" margin="dense" />
                    )}
                    fullWidth
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={() => onConfirm(selectedOption)}>
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function ClassificationResultsPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    // UI state variables.
    const [result, setResult] = useState(null);
    const [leftTopRows, setLeftTopRows] = useState([]); // Rows with confidence < 90% or not validated.
    const [leftBottomRows, setLeftBottomRows] = useState([]); // Rows with confidence ≥ 90% or validated.
    const [selectedRow, setSelectedRow] = useState(null);
    const [loanNumber, setLoanNumber] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [pdfLoading, setPdfLoading] = useState(true);
    const [masterTotalPages, setMasterTotalPages] = useState(null);
    const [currentPdfPage, setCurrentPdfPage] = useState(null);

    // New state variables for validations and dialogs.
    const [showUnclassifiedWarningDialog, setShowUnclassifiedWarningDialog] = useState(false);
    const [showGapDialog, setShowGapDialog] = useState(false);
    const [gapRanges, setGapRanges] = useState([]); // Array of {startPage, endPage}
    const [overlappingRowIds, setOverlappingRowIds] = useState([]); // Array of row ids that have overlaps
    
    // Dialog states.
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [classificationOptions, setClassificationOptions] = useState([]);
    const [splitDialogOpen, setSplitDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const [pendingValidationResolver, setPendingValidationResolver] = useState(null);

    const [loanSelectorOpen, setLoanSelectorOpen] = useState(false);
    const [loanResults, setLoanResults] = useState([]);
    const [selectedLoans, setSelectedLoans] = useState([]);
    const [defaultMilestone, setDefaultMilestone] = useState('Doc Prep');
    const [ignoreDuplicates, setIgnoreDuplicates] = useState(true);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const handleOpenLoanSelector = async () => {
        const passed = await runValidations();
        if (passed) {
            setLoanSelectorOpen(true);
        }
    };
    const handleCloseLoanSelector = () => setLoanSelectorOpen(false);

    const handleSearchLoans = async ({ loanNumbers, borrowerName }) => {
        try {
            // ✅ Clear previous results and show placeholder
            setLoanResults([{ isPlaceholder: true }]);

            const requestBody = {
                loanNumber: loanNumbers.join(','),
                borrowerName
            };

            const res = await fetch('/api/encompass/searchloans', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody)
            });

            if (!res.ok) throw res;

            const data = await res.json();
            setLoanResults(data);
        } catch (error) {
            try {
                const errorText = await error.text();
                let message = '';

                try {
                    const json = JSON.parse(errorText);
                    const messages = Object.values(json.errors || {}).flat();
                    message = messages.join(' ') || json.title || 'Loan search failed.';
                } catch {
                    message = errorText || 'Loan search failed.';
                }

                setSnackbarMessage(message);
            } catch {
                setSnackbarMessage('Loan search failed.');
            }

            setSnackbarOpen(true);
        }
    };

    const handleConfirmUpload = async () => {
        try {
            const response = await fetch('/api/encompass/upload', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    classificationResultId: result?.id,
                    loanNumbers: selectedLoans.map(l => l.fields?.["Loan.LoanNumber"]),
                    defaultMilestone,
                    ignoreFilesAlreadyUploaded: ignoreDuplicates
                })
            });
            if (!response.ok) throw new Error('Upload failed');
            setSnackbarMessage('Upload started successfully');
        } catch (error) {
            console.error('Upload failed:', error);
            setSnackbarMessage('Upload failed');
        } finally {
            setSnackbarOpen(true);
            setLoanSelectorOpen(false);
            setSelectedLoans([]);
            setLoanResults([]);
        }
    };

    // For input values of start and end pages.
    const [startPageInput, setStartPageInput] = useState(selectedRow ? selectedRow.startPage : '');
    const [endPageInput, setEndPageInput] = useState(selectedRow ? selectedRow.endPage : '');

    const [teachDialogOpen, setTeachDialogOpen] = useState(false);
    const [trainingSets, setTrainingSets] = useState([]);
    const [teachMode, setTeachMode] = useState('batch'); // 'batch' or 'single'
    const [teachForm, setTeachForm] = useState({
        trainingSetId: '',
        useCorrected: true,
        maxPerClass: 20,
        overflowPolicy: 'ReplaceLast',
    });

    // Zoom state
    const [zoomLevel, setZoomLevel] = useState(1.0);
    const ZOOM_STEP = 0.25;
    const MIN_ZOOM = 0.5;
    const MAX_ZOOM = 3.0;
    const [pageRotation, setPageRotation] = useState(0);

    const [moreAnchorEl, setMoreAnchorEl] = useState(null);
    const handleMoreClick = (e) => setMoreAnchorEl(e.currentTarget);
    const handleMoreClose = () => setMoreAnchorEl(null);

    const openTeachDialogForSingle = (row) => {
        if (!row || !row.isValidated) {
            setError("Only validated documents can be taught.");
            return;
        }
        setTeachMode('single');
        setSelectedRow(row);
        setTeachDialogOpen(true);
    };


    // Define handleEditClassification to open the edit dialog.
    const handleEditClassification = () => {
        setEditDialogOpen(true);
    };

    const handleRotate = async (newRot) => {
        // 1️⃣ Update the viewer immediately
        setPageRotation(newRot);

        // 2️⃣ Update your in-memory copy of the row
        const updatedRow = { ...selectedRow, rotation: newRot };
        setSelectedRow(updatedRow);
        setLeftTopRows(prev =>
            prev.map(r => (r.id === updatedRow.id ? updatedRow : r))
        );
        setLeftBottomRows(prev =>
            prev.map(r => (r.id === updatedRow.id ? updatedRow : r))
        );

        // 3️⃣ Persist to the server
        try {
            await fetch(
                `/api/classification/documentresults/${updatedRow.id}/rotate`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ rotation: newRot })
                }
            );
        } catch (err) {
            console.error('Rotation save failed', err);
            // optionally roll back: setPageRotation(prev => /* opposite */);
        }
    };


    // Update these values whenever selectedRow changes.
    useEffect(() => {
        if (selectedRow) {
            setStartPageInput(selectedRow.startPage);
            setEndPageInput(selectedRow.endPage);
            setPageRotation(selectedRow.rotation ?? 0);
        }
    }, [selectedRow]);

    // Fetch classification result.
    useEffect(() => {
        async function fetchResult() {
            try {
                const res = await fetch(`/api/classification/results/${id}`, { credentials: 'include' });
                if (!res.ok) throw new Error(await res.text());
                const data = await res.json();
                setResult(data);
                splitRows(data.documentResults);
            } catch (err) {
                setError(err.message);
            }
        }
        fetchResult();
    }, [id]);

    useEffect(() => {
        fetch('/api/TrainingSet', { credentials: 'include' })
            .then(res => res.json())
            .then(data => setTrainingSets(data))
            .catch(err => console.error('Failed to load training sets', err));
    }, []);

    // Fetch classification options.
    useEffect(() => {
        async function fetchOptions() {
            try {
                const res = await fetch('/api/documentclassifications', { credentials: 'include' });
                const data = await res.json();
                data.sort((a, b) => a.label.localeCompare(b.label));
                setClassificationOptions(data);
            } catch (err) {
                console.error('Error fetching classification options', err);
            }
        }
        fetchOptions();
    }, []);

    // Split rows into top and bottom panels.
    const splitRows = (rows) => {
        if (!rows) return;
        const top = [];
        const bottom = [];
        rows.forEach((row) => {
            // Use user override if exists.
            if (!row.isValidated && Number(row.confidence) < 0.9) {
                top.push(row);
            } else {
                bottom.push(row);
            }
        });
        top.sort((a, b) => (a.startPage || 0) - (b.startPage || 0));
        bottom.sort((a, b) => (a.startPage || 0) - (b.startPage || 0));
        setLeftTopRows(top);
        setLeftBottomRows(bottom);
    };

    // When a row is clicked.
    const handleRowClick = (row) => {
        setSelectedRow(row);
        setCurrentPdfPage(row.startPage);
    };

    // Function to update page numbers using the new endpoint.
    const updatePageNumbers = async (newStart, newEnd) => {
        try {
            const res = await fetch('/api/classification/updatePageNumbers', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ParentRecordId: selectedRow.id,
                    StartPage: newStart,
                    EndPage: newEnd,
                }),
            });
            if (!res.ok) throw new Error(await res.text());
            // Optionally, you can parse the response if it returns the updated record.
            // For now, we'll update the local state.
            const updatedRow = { ...selectedRow, startPage: newStart, endPage: newEnd, FeedbackTimestamp: new Date().toISOString(), isCorrect: false };
            setSelectedRow(updatedRow);
            setLeftTopRows((prev) => prev.map((r) => (r.id === updatedRow.id ? updatedRow : r)));
            setLeftBottomRows((prev) => prev.map((r) => (r.id === updatedRow.id ? updatedRow : r)));
        } catch (err) {
            setError(err.message);
        }
    };

    // Function to change the classification using the new endpoint.
    const handleEditConfirm = async (selectedOption) => {
        if (selectedOption && selectedRow) {
            try {
                const res = await fetch('/api/classification/changeClassification', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ParentRecordId: selectedRow.id,
                        CorrectClassificationId: selectedOption.value,
                    }),
                });
                if (!res.ok) throw new Error(await res.text());
                // Update the local record accordingly.
                const updatedRow = {
                    ...selectedRow,
                    correctClassificationId: selectedOption.value,
                    correctClassification: {
                        classificationName: selectedOption.label,
                        aliases: [{ alias: selectedOption.alias }],
                    },
                    isCorrect: false,
                    FeedbackTimestamp: new Date().toISOString(),
                };
                setSelectedRow(updatedRow);
                setLeftTopRows((prev) => prev.map((r) => (r.id === updatedRow.id ? updatedRow : r)));
                setLeftBottomRows((prev) => prev.map((r) => (r.id === updatedRow.id ? updatedRow : r)));
            } catch (err) {
                setError(err.message);
            }
        }
        setEditDialogOpen(false);
    };
    // Update the onBlur handlers for start/end page fields to use updatePageNumbers.
    const handleStartPageChange = async (value) => {
        if (!selectedRow) return;
        let newStart = Number(value);
        if (newStart < 1) newStart = 1;
        if (selectedRow.endPage && newStart > selectedRow.endPage) newStart = selectedRow.endPage;
        await updatePageNumbers(newStart, selectedRow.endPage);
    };

    const handleEndPageChange = async (value) => {
        if (!selectedRow) return;
        let newEnd = Number(value);
        if (masterTotalPages && newEnd > masterTotalPages) newEnd = masterTotalPages;
        if (newEnd < selectedRow.startPage) newEnd = selectedRow.startPage;
        await updatePageNumbers(selectedRow.startPage, newEnd);
    };

    // Function to validate a record using the new validate endpoint.
    const handleValidate = async () => {
        if (!selectedRow) return;
        try {
            const res = await fetch('/api/classification/validate', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ParentRecordId: selectedRow.id }),
            });
            if (!res.ok) throw new Error(await res.text());
            // After validation, update the record locally.
            const updatedRow = {
                ...selectedRow,
                isValidated: true,
                validatedAt: new Date().toISOString(),
                validatedById: "currentUser"
            };
            setLeftTopRows((prev) => prev.filter((r) => r.id !== updatedRow.id));
            setLeftBottomRows((prev) => {
                const exists = prev.find((r) => r.id === updatedRow.id);
                if (!exists) return [...prev, updatedRow].sort((a, b) => (a.startPage || 0) - (b.startPage || 0));
                return prev.map((r) => (r.id === updatedRow.id ? updatedRow : r));
            });
            setSelectedRow(updatedRow);
        } catch (err) {
            setError(err.message);
        }
    };

    // Split functionality.
    const handleConfirmSplit = async () => {
        if (!selectedRow || !currentPdfPage) return;
        const { startPage, endPage } = selectedRow;
        const payload = {
            ParentRecordId: selectedRow.id,
            SplitPage: currentPdfPage,
            TotalPages: endPage - startPage + 1,
        };
        try {
            const res = await fetch('/api/classification/split', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await res.text());
            const updateResponse = await res.json();
            setLeftTopRows((prev) => {
                let updated = prev.filter((r) => r.id !== selectedRow.id);
                updated.push(updateResponse.updatedRow);
                updated.push(updateResponse.newRow);
                return updated.sort((a, b) => (a.startPage || 0) - (b.startPage || 0));
            });
            setLeftBottomRows((prev) => prev.filter((r) => r.id !== selectedRow.id));
            setSelectedRow(updateResponse.updatedRow);
        } catch (err) {
            setError(err.message);
        }
        setSplitDialogOpen(false);
    };

    const handleCancelSplit = () => {
        setSplitDialogOpen(false);
    };

    // Delete record.
    const handleDeleteRowConfirmed = async () => {
        if (selectedRow) {
            try {
                const res = await fetch(`/api/classification/documentresults/${selectedRow.id}`, {
                    method: 'DELETE',
                    credentials: 'include',
                });
                if (!res.ok) throw new Error(await res.text());
                setLeftTopRows((prev) => prev.filter((r) => r.id !== selectedRow.id));
                setLeftBottomRows((prev) => prev.filter((r) => r.id !== selectedRow.id));
                setSelectedRow(null);
            } catch (err) {
                setError(err.message);
            }
        }
        setDeleteDialogOpen(false);
    };

    

    // Helper: Check for overlapping page ranges.
    const checkOverlappingRows = () => {
        const combined = [...leftTopRows, ...leftBottomRows].sort((a, b) => (a.startPage || 0) - (b.startPage || 0));
        const overlaps = new Set();
        for (let i = 0; i < combined.length - 1; i++) {
            const current = combined[i];
            const next = combined[i + 1];
            if (current.endPage >= next.startPage) {
                overlaps.add(current.id);
                overlaps.add(next.id);
            }
        }
        return Array.from(overlaps);
    };

    // Helper: Check for gaps in page ranges.
    const checkForGaps = () => {
        const combined = [...leftTopRows, ...leftBottomRows].sort((a, b) => (a.startPage || 0) - (b.startPage || 0));
        const gaps = [];

        if (combined.length === 0 && masterTotalPages) {
            gaps.push({ startPage: 1, endPage: masterTotalPages });
            return gaps;
        }

        const overallStart = 1;
        const overallEnd = combined[combined.length - 1].endPage;
        let currentPage = overallStart;

        combined.forEach((row) => {
            if (row.startPage > currentPage) {
                gaps.push({ startPage: currentPage, endPage: row.startPage - 1 });
            }
            currentPage = row.endPage + 1;
        });

        if (currentPage <= masterTotalPages) {
            gaps.push({ startPage: currentPage, endPage: masterTotalPages });
        }

        return gaps;
    };



    // Function to create gap records via an API call.
    const createGapRecords = async (gaps) => {
        for (let gap of gaps) {
            // If no original record, use the first available classification option as fallback.
            const fallbackClassification = classificationOptions.length > 0
                ? classificationOptions[0]
                : { value: null, label: "Default" };

            const payload = {
                ClassificationResultId: result.id,
                StartPage: gap.startPage,
                EndPage: gap.endPage,
                Confidence: 0,
                // Use the fallback classification for these fields.
                ClassificationId: fallbackClassification.value,
                DocType: fallbackClassification.label,
            };

            try {
                const res = await fetch('/api/classification/gap', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                if (!res.ok) {
                    throw new Error(await res.text());
                }
                const newRow = await res.json();
                setLeftTopRows((prev) =>
                    [...prev, newRow].sort((a, b) => (a.startPage || 0) - (b.startPage || 0))
                );
            } catch (err) {
                setError(`Error creating gap record for pages ${gap.startPage}-${gap.endPage}: ${err.message}`);
            }
        }
    };

    const runValidations = () => {
        return new Promise((resolve) => {
            setPendingValidationResolver(() => resolve);

            // Step 1: Overlaps
            const overlaps = checkOverlappingRows();
            if (overlaps.length > 0) {
                setOverlappingRowIds(overlaps);
                setError('Overlapping page ranges detected. Please resolve the highlighted rows.');
                resolve(false);
                return;
            }

            // Step 2: Gaps
            const gaps = checkForGaps();
            if (gaps.length > 0) {
                setGapRanges(gaps);
                setShowGapDialog(true);
                resolve(false);
                return;
            }

            // Step 3: Unclassified
            if (leftTopRows.length > 0) {
                setShowUnclassifiedWarningDialog(true);
                return; // resolver is called from the dialog
            }

            resolve(true);
        });
    };
  
    
    // Handler when user confirms the gap dialog.
    const handleConfirmGapDialog = async () => {
        setShowGapDialog(false);
        await createGapRecords(gapRanges);
        // Instead of automatically pushing again, we show an informational message
        setSuccessMsg('Gap records created. Please review and edit them as needed before pushing to LOS.');
        // Optionally, you can also clear any error or reset validation flags if needed.
    };

    const handleTeachSubmit = async () => {
        if (!result?.id || !teachForm.trainingSetId) {
            setError('Missing required information.');
            return;
        }
        try {
            const payload = {
                trainingSetId: teachForm.trainingSetId,
                useCorrected: teachForm.useCorrected,
                maxPerClassification: teachForm.maxPerClass,
                overflowPolicy: teachForm.overflowPolicy,
            };

            if (teachMode === 'batch') {
                payload.context = 'ClassificationResults';
                payload.classificationResultId = result.id;
            } else {
                payload.context = 'ClassificationDocumentResults';
                payload.classificationDocumentResultId = selectedRow?.id;
            }

            const res = await fetch('/api/TrainingSetTeaching/teach', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error(await res.text());
            setSuccessMsg('Teaching workflow started.');
            setTeachDialogOpen(false);
        } catch (err) {
            setError(err.message);
        }
    };


    // When PDF loads.
    const handlePdfLoadSuccess = (numPages) => {
        setPdfLoading(false);
        setMasterTotalPages(numPages);
    };

    // Navigation.
    const handleNavigateAway = useCallback(
        (url) => {
            navigate(url);
        },
        [navigate]
    );

    const downloadUrl = result
        ? `${window.location.origin}/api/classification/results/${result.id}/download`
        : '';

    const viewerProps = selectedRow
        ? { downloadUrl, startPage: Number(selectedRow.startPage), endPage: Number(selectedRow.endPage) }
        : { downloadUrl, startPage: 1, endPage: undefined };

    const renderClassification = (row) => {
        if (row.correctClassificationId && row.correctClassification && row.correctClassification.classificationName) {
            return row.correctClassification.classificationName;
        }
        if (row.classification && row.classification.classificationName) {
            return row.classification.classificationName;
        }
        return "Unknown Classification";
    };

    const renderLosDoc = (row) => {
        if (row.correctClassificationId && row.correctClassification && row.correctClassification.aliases && row.correctClassification.aliases[0]) {
            return row.correctClassification.aliases[0].alias;
        }
        if (row.classification && row.classification.aliases && row.classification.aliases[0]) {
            return row.classification.aliases[0].alias;
        }
        return "";
    };

    // Utility to conditionally highlight overlapping rows.
    const getRowStyle = (rowId) => {
        return overlappingRowIds.includes(rowId)
            ? { backgroundColor: '#FFCDD2' } // light red for highlighting overlaps
            : {};
    };

    return (
        <PageLayout
            title=""
            breadcrumb={[
                { label: 'Home', to: '/' },
                { label: 'Documents', to: '/documents' },
                { label: result ? result.fileName : 'Loading...' },
            ]}
        >
            {/* Header */}
            <Paper sx={{ p: 1, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">{result ? result.fileName : 'Loading file...'}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>

                    

                    <Button variant="contained" onClick={handleOpenLoanSelector}>
                        Upload to LOS
                    </Button>

                    <Tooltip title="Validate Document Classifications">
                        <IconButton onClick={async () => {
                            const passed = await runValidations();
                            if (passed) {
                                setSuccessMsg("Validation passed!");
                            }
                        }}>
                            <FactCheckIcon />
                        </IconButton>
                    </Tooltip>

                    <LoanSelectorDialog
                        open={loanSelectorOpen}
                        onClose={handleCloseLoanSelector}
                        onConfirm={handleConfirmUpload}
                        onSearch={handleSearchLoans}
                        loanResults={loanResults}
                        selectedLoans={selectedLoans}
                        setSelectedLoans={setSelectedLoans}
                        defaultMilestone={defaultMilestone}
                        setDefaultMilestone={setDefaultMilestone}
                        ignoreDuplicates={ignoreDuplicates}
                        setIgnoreDuplicates={setIgnoreDuplicates}
                    />

                    <Snackbar
                        open={snackbarOpen}
                        autoHideDuration={4000}
                        onClose={() => setSnackbarOpen(false)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert severity="info" sx={{ width: '100%' }}>{snackbarMessage}</Alert>
                    </Snackbar>


                    <Tooltip title="Teach AI Model">
                        <IconButton onClick={() => { setTeachMode('batch'); setTeachDialogOpen(true); }}>
                            <SchoolIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Paper>

            <Grid container spacing={2}>
                {/* Left Panels */}
                <Grid item xs={12} md={5}>
                    <Box sx={{ mb: 1 }}>
                        <Paper sx={{ p: 0.5, height: 300, overflowY: 'auto' }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 'bold',
                                    color: '#333333',
                                    borderBottom: '1px solid #ccc',
                                    display: 'block',
                                    mb: 0.5,
                                    py: 0.25,
                                }}
                            >
                                Classifications below threshold (&lt;90%)
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ py: 0.25, fontWeight: 'bold', fontSize: '12px', color: '#333333' }}>
                                            Classification
                                        </TableCell>
                                        <TableCell sx={{ py: 0.25, fontWeight: 'bold', fontSize: '12px', color: '#333333' }}>
                                            Pages
                                        </TableCell>
                                        <TableCell sx={{ py: 0.25, fontWeight: 'bold', fontSize: '12px', color: '#333333' }}>
                                            LOS Doc
                                        </TableCell>
                                        <TableCell sx={{ py: 0.25, fontWeight: 'bold', fontSize: '12px', color: '#333333' }} align="center">
                                            Confidence
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {leftTopRows.map((row) => (
                                        <TableRow key={row.id} hover onClick={() => handleRowClick(row)} selected={selectedRow && selectedRow.id === row.id} sx={{ cursor: 'pointer', ...getRowStyle(row.id) }}>
                                            <TableCell sx={{ py: 0.25, fontSize: '0.8rem' }}>{renderClassification(row)}</TableCell>
                                            <TableCell sx={{ py: 0.25, fontSize: '0.8rem' }}>{`${row.startPage}-${row.endPage}`}</TableCell>
                                            <TableCell sx={{ py: 0.25, fontSize: '0.8rem' }}>{renderLosDoc(row)}</TableCell>
                                            <TableCell sx={{ py: 0.25, fontSize: '0.8rem' }} align="center">
                                                {(row.confidence * 100).toFixed(1)}%
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Box>
                    <Box>
                        <Paper sx={{ p: 0.5, height: 510, overflowY: 'auto' }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 'bold',
                                    color: '#333333',
                                    borderBottom: '1px solid #ccc',
                                    display: 'block',
                                    mb: 0.5,
                                    py: 0.25,
                                }}
                            >
                                Classifications meeting threshold (≥90%) or validated
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ py: 0.25, fontWeight: 'bold', fontSize: '12px', color: '#333333' }}>
                                            Classification
                                        </TableCell>
                                        <TableCell sx={{ py: 0.25, fontWeight: 'bold', fontSize: '12px', color: '#333333' }}>
                                            Pages
                                        </TableCell>
                                        <TableCell sx={{ py: 0.25, fontWeight: 'bold', fontSize: '12px', color: '#333333' }}>
                                            LOS Doc
                                        </TableCell>
                                        <TableCell sx={{ py: 0.25, fontWeight: 'bold', fontSize: '12px', color: '#333333' }} align="center">
                                            Confidence
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {leftBottomRows.map((row) => (
                                        <TableRow key={row.id} hover onClick={() => handleRowClick(row)} selected={selectedRow && selectedRow.id === row.id} sx={{ cursor: 'pointer', ...getRowStyle(row.id) }}>
                                            <TableCell sx={{ py: 0.25, fontSize: '0.8rem' }}>{renderClassification(row)}</TableCell>
                                            <TableCell sx={{ py: 0.25, fontSize: '0.8rem' }}>{`${row.startPage}-${row.endPage}`}</TableCell>
                                            <TableCell sx={{ py: 0.25, fontSize: '0.8rem' }}>{renderLosDoc(row)}</TableCell>
                                            <TableCell sx={{ py: 0.25, fontSize: '0.8rem' }} align="center">
                                                {(row.confidence * 100).toFixed(1)}%
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Box>
                </Grid>

                {/* Right Panel: PDF Viewer and toolbar */}
                <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 1, position: 'relative', height: '80vh' }}>
                        {/* Toolbar */}
                        <Box
                            sx={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: 'white',
                                zIndex: 10,
                                p: 1,
                                borderBottom: '1px solid #ddd',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            {selectedRow ? (

                                <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        px: 1,
                                        py: 0.5
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="subtitle1">
                                            {selectedRow.correctClassificationId && selectedRow.correctClassification
                                                ? selectedRow.correctClassification.classificationName
                                                : selectedRow.classification.classificationName}
                                        </Typography>
                                        <IconButton size="small" onClick={handleEditClassification} color="primary">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ) : (
                                <Typography variant="subtitle1">{result ? result.fileName : 'Loading...'}</Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {selectedRow && (
                                    <>
                                        <TextField
                                            label="Start"
                                            size="small"
                                            value={startPageInput || ''}
                                            onChange={(e) => setStartPageInput(e.target.value)}
                                            onBlur={() => {
                                                let newStart = Number(startPageInput);
                                                if (isNaN(newStart) || newStart < 1) {
                                                    newStart = 1;
                                                }
                                                if (selectedRow.endPage && newStart > selectedRow.endPage) {
                                                    newStart = selectedRow.endPage;
                                                }
                                                handleStartPageChange(newStart);
                                            }}
                                            sx={{ width: 60 }}
                                        />
                                        <TextField
                                            label="End"
                                            size="small"
                                            value={endPageInput || ''}
                                            onChange={(e) => setEndPageInput(e.target.value)}
                                            onBlur={() => {
                                                let newEnd = Number(endPageInput);
                                                if (selectedRow && selectedRow.startPage && newEnd < selectedRow.startPage) {
                                                    newEnd = selectedRow.startPage;
                                                }
                                                if (masterTotalPages && newEnd > masterTotalPages) {
                                                    newEnd = masterTotalPages;
                                                }
                                                handleEndPageChange(newEnd);
                                            }}
                                            sx={{ width: 60 }}
                                        />
                                        {currentPdfPage > selectedRow.startPage && (
                                            <IconButton size="small" onClick={() => setSplitDialogOpen(true)} sx={{ color: 'primary.main' }}>
                                                <ContentCutIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        <Tooltip title="Delete this Classification">
                                            <IconButton sx={{ ml: 1 }} onClick={() => setDeleteDialogOpen(true)} color="primary">
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Set Classification as Validated">
                                            <IconButton size="small" onClick={handleValidate} color="success">
                                                <CheckCircleIcon />
                                            </IconButton>
                                        </Tooltip>

                                        {/* 6) More… */}
                                        <IconButton
                                            size="small"
                                            onClick={handleMoreClick}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>

                                        <Menu
                                            anchorEl={moreAnchorEl}
                                            open={Boolean(moreAnchorEl)}
                                            onClose={handleMoreClose}
                                        >
                                            <MenuItem
                                                onClick={async () => {
                                                    const newRot = (pageRotation - 90 + 360) % 360;
                                                    handleRotate(newRot);
                                                    handleMoreClose();
                                                }}
                                            >
                                                <ListItemIcon><RotateLeftIcon fontSize="small" /></ListItemIcon>
                                                <ListItemText primary="Rotate Left" />
                                            </MenuItem>
                                            <MenuItem
                                                onClick={async () => {
                                                    const newRot = (pageRotation + 90) % 360;
                                                    handleRotate(newRot);
                                                    handleMoreClose();
                                                }}
                                            >
                                                <ListItemIcon><RotateRightIcon fontSize="small" /></ListItemIcon>
                                                <ListItemText primary="Rotate Right" />
                                            </MenuItem>
                                            <Divider />
                                            <MenuItem
                                                onClick={() => {
                                                    setZoomLevel(z => Math.max(z - ZOOM_STEP, MIN_ZOOM));
                                                    handleMoreClose();
                                                    }}
                                                >
                                                <ListItemIcon><ZoomOutIcon fontSize="small" /></ListItemIcon>
                                                <ListItemText primary="Zoom Out" />
                                            </MenuItem>
                                            <MenuItem
                                                onClick={() => {
                                                    setZoomLevel(z => Math.min(z + ZOOM_STEP, MAX_ZOOM));
                                                    handleMoreClose();
                                                 }}
                                              >
                                              <ListItemIcon><ZoomInIcon fontSize="small" /></ListItemIcon>
                                              <ListItemText primary="Zoom In" />
                                            </MenuItem>
                                            <Divider />
                                            <MenuItem
                                              onClick={() => {
                                                openTeachDialogForSingle(selectedRow);
                                                handleMoreClose();
                                              }}
                                            >
                                              <ListItemIcon><SchoolIcon fontSize="small" /></ListItemIcon>
                                              <ListItemText primary="Teach AI Model" />
                                            </MenuItem>
                                        </Menu>
                                    </>
                                )}
                                {selectedRow && (
                                    <IconButton size="small" onClick={() => setSelectedRow(null)}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>

                        {/* PDF Viewer Area */}
                        <Box sx={{ height: 'calc(80vh - 60px)', overflow: 'auto', position: 'relative' }}>
                            {pdfLoading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(200,200,200,0.5)',
                                        zIndex: 5,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography variant="h6">Loading PDF</Typography>
                                </Box>
                            )}
                            {result && downloadUrl && (
                                <SimplePdfViewer
                                    downloadUrl={downloadUrl}
                                    startPage={viewerProps.startPage}
                                    endPage={viewerProps.endPage}
                                    onLoadSuccess={handlePdfLoadSuccess}
                                    onPageChange={(page) => setCurrentPdfPage(page)}
                                    zoomLevel={zoomLevel}
                                    rotateLevel={pageRotation}
                                />
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <EditClassificationDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                onConfirm={handleEditConfirm}
                initialValue={
                    selectedRow && selectedRow.correctClassificationId && selectedRow.classification
                        ? {
                            value: selectedRow.correctClassificationId,
                            label: selectedRow.classification.classificationName,
                            alias:
                                selectedRow.classification.aliases && selectedRow.classification.aliases.length > 0
                                    ? selectedRow.classification.aliases[0].alias
                                    : '',
                        }
                        : null
                }
                classificationOptions={classificationOptions}
            />

            {/* Split Confirmation Dialog */}
            <Dialog open={splitDialogOpen} onClose={handleCancelSplit}>
                <DialogTitle>Confirm Split</DialogTitle>
                <DialogContent>
                    <Typography>Do you want to split the document before page {currentPdfPage}?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelSplit}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleConfirmSplit}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this classification?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteRowConfirmed}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Unclassified Warning Dialog */}
            <Dialog open={showUnclassifiedWarningDialog} onClose={() => {
                setShowUnclassifiedWarningDialog(false);
                if (pendingValidationResolver) pendingValidationResolver(false);
            }}>
                <DialogTitle>Warning</DialogTitle>
                <DialogContent>
                    <Typography>
                        There are unvalidated or low confidence classifications in the top panel.
                        These files will not be transferred. Do you want to continue?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShowUnclassifiedWarningDialog(false);
                            if (pendingValidationResolver) pendingValidationResolver(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setShowUnclassifiedWarningDialog(false);
                            if (pendingValidationResolver) pendingValidationResolver(true);
                        }}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Gap Confirmation Dialog */}
            <Dialog open={showGapDialog} onClose={() => setShowGapDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>Page Gaps Detected</DialogTitle>
                <DialogContent>
                    <Typography>Gaps in the page ranges were detected:</Typography>
                    {gapRanges.map((gap, index) => (
                        <Typography key={index}>
                            Pages {gap.startPage} - {gap.endPage}
                        </Typography>
                    ))}
                    <Typography sx={{ mt: 1 }}>
                        These gaps will be added as new records with 0% confidence using the first available classification.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowGapDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleConfirmGapDialog}>
                        Create Gaps & Continue
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={teachDialogOpen} onClose={() => setTeachDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Teach AI Model</DialogTitle>
                <DialogContent>
                    {teachMode === 'single' ? (
                        <Typography sx={{ mb: 2 }}>
                            This will add only the selected document to a training set. The document must already be validated.
                        </Typography>
                    ) : (
                        <Typography sx={{ mb: 2 }}>
                            This feature adds documents from this classification result to a training set. Only validated documents are considered.
                            You can control how many documents per classification are used, and how to handle overflows.
                        </Typography>
                    )}

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Training Set</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Choose the training set you want to teach. This set will be updated with new examples.
                    </Typography>
                    <Autocomplete
                        options={trainingSets}
                        getOptionLabel={(option) => option.name}
                        value={trainingSets.find(ts => ts.trainingSetId === teachForm.trainingSetId) || null}
                        onChange={(e, newVal) => setTeachForm({ ...teachForm, trainingSetId: newVal?.trainingSetId || '' })}
                        renderInput={(params) => <TextField {...params} label="Training Set" margin="dense" />}
                        fullWidth
                    />

                    {teachMode !== 'single' && (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 2 }}>Document Source</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Decide whether to include all validated documents or just those that have been manually corrected.
                            </Typography>
                            <Autocomplete
                                options={[{
                                    label: 'Only Corrected Documents in this Classification Result', value: true
                                }, {
                                    label: 'All Validated Documents in this Classification Result', value: false
                                }]}
                                getOptionLabel={(o) => o.label}
                                value={teachForm.useCorrected
                                    ? { label: 'Only Corrected Documents in this Classification Result', value: true }
                                    : { label: 'All Validated Documents in this Classification Result', value: false }}
                                onChange={(e, val) => setTeachForm({ ...teachForm, useCorrected: val?.value })}
                                renderInput={(params) => <TextField {...params} label="Document Source" margin="dense" />}
                                fullWidth
                            />
                        </>
                    )}

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Max Documents per Classification</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Maximum number of validated documents to keep per classification. Prevents overloading the training set with duplicates.
                    </Typography>
                    <TextField
                        type="number"
                        label="Max Documents per Classification"
                        value={teachForm.maxPerClass}
                        onChange={(e) => setTeachForm({ ...teachForm, maxPerClass: Number(e.target.value) })}
                        fullWidth
                        margin="dense"
                    />

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>If Maximum Reached</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        What should happen if a classification already has the maximum allowed number of documents?
                    </Typography>
                    <Autocomplete
                        options={[{
                            label: 'Ignore the document (do not add it)', value: 'Ignore'
                        }, {
                            label: 'Replace the oldest validated document in that classification', value: 'ReplaceFirst'
                        }, {
                            label: 'Replace the most recent validated document in that classification', value: 'ReplaceLast'
                        }]}
                        getOptionLabel={(o) => o.label}
                        value={[{
                            label: 'Ignore the document (do not add it)', value: 'Ignore'
                        }, {
                            label: 'Replace the oldest validated document in that classification', value: 'ReplaceFirst'
                        }, {
                            label: 'Replace the most recent validated document in that classification', value: 'ReplaceLast'
                        }].find(o => o.value === teachForm.overflowPolicy)}
                        onChange={(e, val) => setTeachForm({ ...teachForm, overflowPolicy: val?.value })}
                        renderInput={(params) => <TextField {...params} label="If Maximum Reached" margin="dense" />}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTeachDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleTeachSubmit}>Teach</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar Alerts */}
            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')}>
                    {error}
                </Alert>
            </Snackbar>
            <Snackbar open={!!successMsg} autoHideDuration={6000} onClose={() => setSuccessMsg('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="success" onClose={() => setSuccessMsg('')}>
                    {successMsg}
                </Alert>
            </Snackbar>
        </PageLayout>
    );
}
